import { JobState } from '@piccolohealth/pbs-common';
import { ChakraV3 } from '@piccolohealth/ui';
import React from 'react';
import { FaDownload } from 'react-icons/fa';
import { useAppContext } from '../../hooks/useAppContext';
import { useParticipantsExportJob } from '../../hooks/useJob';
import type { ParticipantsFilter } from '../../hooks/useParticipantsFilter';
import { usePermission } from '../../hooks/usePermission';

interface Props {
  filter: ParticipantsFilter;
}

export const ParticipantsExportMenuItem = (props: Props) => {
  const { filter } = props;
  const { organization, promiseToast } = useAppContext();

  const hasPermission = usePermission('read', 'participant');

  const exportJob = useParticipantsExportJob();

  const onClick = React.useCallback(async () => {
    const promise = async (): Promise<string> => {
      const job = await exportJob.start({
        organizationId: organization.id,
        request: {
          start: filter.startDateFilter,
          end: filter.endDateFilter,
          ndisNumber: filter.ndisNumberFilter,
          labels: filter.labelFilter,
          location: filter.locationFilter,
          assignedTo: filter.assignedToFilter,
          name: filter.nameFilter,
          status: filter.statusFilter,
        },
      });

      if (job?.state !== JobState.Completed) {
        throw new Error('Participants export was not generated');
      }

      return job.response.url;
    };

    await promiseToast(promise, {
      loading: {
        title: 'Generating participants export...',
      },
      success: (url) => ({
        title: 'Participants export successfully generated',
        description: 'Your archive is ready. Download it before closing this message.',
        duration: Number.POSITIVE_INFINITY,
        closable: true,
        action: {
          label: 'Download',
          onClick: () => window.open(url, '_self'),
        },
      }),
      error: {
        title: 'Error exporting participants',
      },
    });
  }, [exportJob, organization.id, filter, promiseToast]);

  return (
    <ChakraV3.MenuItem onClick={onClick} disabled={!hasPermission} value='export-participants'>
      <FaDownload />
      Export participants (ZIP)
    </ChakraV3.MenuItem>
  );
};
